import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function useTimedStateSequence<T>(
  states: readonly { state: T; duration: number }[],
): [T | null, () => void] {
  const stateStr = JSON.stringify(states);
  const memoizedState = useMemo(() => JSON.parse(stateStr), [stateStr]);
  const [currentState, setCurrentState] = useState<T | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const moveToState = useCallback(
    (stateNum: number) => {
      clearTimeout(timeoutRef.current);
      setCurrentState(memoizedState[stateNum].state);

      timeoutRef.current = setTimeout(() => {
        if (stateNum + 1 < memoizedState.length) {
          moveToState(stateNum + 1);
        } else {
          setCurrentState(null); // Reset to null after the last state
        }
      }, memoizedState[stateNum].duration);
    },
    [memoizedState],
  );

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const trigger = useCallback(() => {
    moveToState(0);
  }, [moveToState]);

  return [currentState, trigger];
}
